import { InputPrice } from '@/components/element/Form/InputPrice';
import { useInvalidatePaymentTaskExpenseAccountItems } from '@/hooks/api/2350_payment_task_expense_account_items';
import { useUpdatePaymentTaskExpenseAccountItem } from '@/hooks/api/2370_update_payment_task_expense_account_item';
import { PaymentTaskExpenseAccountItem } from '@/hooks/api/models';
import { toastApiError } from '@/lib/toast';
import { useLoading } from '@/providers/LoadingProvider';
import { useCurrentPaymentTask } from '@/providers/PaymentTaskProvider';
import { useCurrentTeamMember } from '@/providers/TeamMemberProvider';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

type PaymentTaskExpenseAccountItemFormInputs = {
  identifier: string;
  team_id: number;
  purchase_order_id: number;
  task_id: number;
  id: number;
  amount: number;
};

export const PaymentTaskExpenseAccountItemRow = ({
  d,
  setDelTarget,
}: {
  d: PaymentTaskExpenseAccountItem;
  setDelTarget: Dispatch<SetStateAction<PaymentTaskExpenseAccountItem | undefined>>;
}) => {
  const { setShowLoading } = useLoading();
  const { _w_, _t_, me } = useCurrentTeamMember();
  const { purchase_order_id } = useParams();
  const { paymentTask: task, setPaymentTask } = useCurrentPaymentTask();
  const [editing, setEditing] = useState<boolean>(false);
  const { mutate, data, isSuccess, error, isPending } = useUpdatePaymentTaskExpenseAccountItem();
  const { invalidate } = useInvalidatePaymentTaskExpenseAccountItems({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    purchase_order_id: Number(purchase_order_id),
    task_id: task!.id,
  });
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<PaymentTaskExpenseAccountItemFormInputs>({
    mode: 'onSubmit',
    criteriaMode: 'all',
    defaultValues: {
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      purchase_order_id: Number(purchase_order_id),
      task_id: task!.id,
      id: d.id,
      amount: d.amount,
    },
  });

  useEffect(() => setShowLoading(isPending), [isPending]);
  useEffect(() => toastApiError(error), [error]);

  const onSubmit: SubmitHandler<PaymentTaskExpenseAccountItemFormInputs> = (inputData) => {
    setShowLoading(true);
    mutate(inputData);
  };

  useEffect(() => {
    if (isSuccess && data) {
      setEditing(false);
      invalidate();
    }
  }, [isSuccess]);

  return (
    <tr>
      <td>
        <div className="d-flex align-items-center">
          <div className="">
            <div className="font-weight-bold px-0 text-sm text-dark mb-1">{d.expense_account_item.label}</div>
          </div>
        </div>
      </td>
      {editing && (
        <>
          <td className="text-end text-dark text-xs">
            <div className="input-group">
              <Controller
                render={({ field: { onChange, value, ...field } }) => <InputPrice onChange={onChange} value={value} error={errors.amount} />}
                control={control}
                name="amount"
                rules={{ required: true }}
              />
            </div>
          </td>
          <td className="text-end">
            <button className="btn btn-icon-only btn-outline-secondary me-1" onClick={() => setEditing(false)}>
              <i className="fas fa-undo"></i>
            </button>
            <button className="btn btn-icon-only bg-gradient-dark" onClick={() => handleSubmit(onSubmit)()}>
              <i className="fas fa-check-circle"></i>
            </button>
          </td>
        </>
      )}
      {!editing && (
        <>
          <td className="text-end text-dark text-xs">¥{d.amount.toLocaleString()}</td>
          <td className="text-end">
            <button className="btn btn-icon-only bg-gradient-success me-1" onClick={() => setEditing(true)}>
              <i className="fas fa-edit"></i>
            </button>
            <button
              className="btn btn-icon-only bg-gradient-danger"
              data-bs-toggle="modal"
              data-bs-target={`#delConfirm`}
              onClick={() => setDelTarget((_) => d)}
            >
              <i className="fas fa-trash-alt"></i>
            </button>
          </td>
        </>
      )}
    </tr>
  );
};
